<script setup lang="ts">
import { ref, computed, onUnmounted, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useIncidentStore } from '@/stores/incident'
import { useAuthStore } from '@/stores/auth'
import { usePersonnelStore } from '@/stores/personnel'
import { useTeamsStore } from '@/stores/teams'
import { useOpsStore } from '@/stores/ops'
import { useIwiStore } from '@/stores/iwi'
import { usePriorityTrafficStore } from '@/stores/priorityTraffic'
import { useToastStore } from '@/stores/toast'
import { usePermissionsStore } from '@/stores/permissions'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import { IncidentStatus } from '@/types'
import UserAvatar from '@/components/common/UserAvatar.vue'
import CheckInModal from '@/components/personnel/CheckInModal.vue'
import HeaderOpSelector from '@/components/layout/header/HeaderOpSelector.vue'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosAlertDialog from '@/components/ui/FosAlertDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosSelect from '@/components/ui/FosSelect.vue'
import ThemeToggle from '@/components/layout/header/ThemeToggle.vue'

const router = useRouter()
const route = useRoute()
const incidentStore = useIncidentStore()
const authStore = useAuthStore()
const personnelStore = usePersonnelStore()
const teamsStore = useTeamsStore()
const opsStore = useOpsStore()
const iwiStore = useIwiStore()
const ptiStore = usePriorityTrafficStore()
const toast = useToastStore()
const permissionsStore = usePermissionsStore()
const { opRole } = useCurrentOpRole()

const isInsideIncident = computed(() => !!route.params.id)

// Inline incident name editing
const canEditIncidentName = computed(() =>
  permissionsStore.can('incident.edit', opRole.value) &&
  incident.value?.status !== IncidentStatus.Closed,
)
const isEditingName = ref(false)
const editingName = ref('')
const nameInputEl = ref<HTMLInputElement | null>(null)

async function startEditName() {
  if (!canEditIncidentName.value || !incident.value) return
  editingName.value = incident.value.incident_name ?? ''
  isEditingName.value = true
  await nextTick()
  nameInputEl.value?.select()
}

async function commitEditName() {
  if (!isEditingName.value) return
  isEditingName.value = false
  const trimmed = editingName.value.trim()
  const incidentId = route.params.id as string
  if (!trimmed || trimmed === incident.value?.incident_name) return
  await incidentStore.updateIncident(incidentId, { incident_name: trimmed })
}

function cancelEditName() {
  isEditingName.value = false
}

function onNameKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter') { e.preventDefault(); commitEditName() }
  if (e.key === 'Escape') cancelEditName()
}

// Manual IWI declare dialog
const showDeclareDialog = ref(false)
const selectedTeamId = ref('')
const declaring = ref(false)

const teamOptions = computed(() =>
  teamsStore.teams.map((t) => ({ label: `Team ${t.team_identifier}`, value: t.team_id })),
)

async function confirmDeclareIwi() {
  const incidentId = route.params.id as string
  const opId = opsStore.selectedOpId
  if (!opId || !selectedTeamId.value) return
  const team = teamsStore.teams.find((t) => t.team_id === selectedTeamId.value)
  if (!team) return
  declaring.value = true
  try {
    await iwiStore.createIwi(incidentId, opId, {
      declared_by_team_id: team.team_id,
      declared_by_team_identifier: team.team_identifier,
      triggering_radio_log_entry_id: null,
    })
    showDeclareDialog.value = false
    selectedTeamId.value = ''
  } catch {
    toast.add('Failed to declare IWI', 'error')
  } finally {
    declaring.value = false
  }
}

const iwiElapsed = ref('0:00')
let iwiTimer: ReturnType<typeof setInterval> | null = null

function startIwiTimer() {
  if (iwiTimer) clearInterval(iwiTimer)
  iwiTimer = setInterval(() => {
    const iwi = iwiStore.activeIwi
    if (!iwi?.declared_at) return
    const elapsed = Date.now() - iwi.declared_at.toDate().getTime()
    const totalSecs = Math.floor(elapsed / 1000)
    const mins = Math.floor(totalSecs / 60)
    const secs = totalSecs % 60
    iwiElapsed.value = `${mins}:${String(secs).padStart(2, '0')}`
  }, 1000)
}

watch(
  () => iwiStore.activeIwi,
  (iwi) => {
    if (iwi) {
      startIwiTimer()
    } else {
      if (iwiTimer) clearInterval(iwiTimer)
      iwiTimer = null
      iwiElapsed.value = '0:00'
    }
  },
  { immediate: true },
)

const ptiElapsed = ref('0:00')
let ptiTimer: ReturnType<typeof setInterval> | null = null

watch(
  () => ptiStore.activePti,
  (pti) => {
    if (ptiTimer) clearInterval(ptiTimer)
    ptiTimer = null
    if (pti?.declared_at) {
      ptiTimer = setInterval(() => {
        const totalSecs = Math.floor((Date.now() - pti.declared_at.toDate().getTime()) / 1000)
        ptiElapsed.value = `${Math.floor(totalSecs / 60)}:${String(totalSecs % 60).padStart(2, '0')}`
      }, 1000)
    } else {
      ptiElapsed.value = '0:00'
    }
  },
  { immediate: true },
)

function navigateToOverview() {
  const incidentId = route.params.id as string | undefined
  if (incidentId) {
    router.push({ name: 'incident-overview', params: { id: incidentId } })
  }
}

const showCheckIn = ref(false)
const incident = computed(() => incidentStore.currentIncident)
const showCheckOutConfirm = ref(false)

async function confirmSelfCheckOut() {
  const record = personnelStore.myRecord
  const incidentId = route.params.id as string
  const opId = opsStore.selectedOpId
  if (!record || !opId) return
  await personnelStore.checkOut(incidentId, opId, record.personnel_id)
  showCheckOutConfirm.value = false
}

onUnmounted(() => {
  if (iwiTimer) clearInterval(iwiTimer)
  if (ptiTimer) clearInterval(ptiTimer)
})

function statusLabel(status: IncidentStatus | undefined): string {
  if (status === IncidentStatus.Active) return 'ACTIVE'
  if (status === IncidentStatus.Suspended) return 'SUSPENDED'
  if (status === IncidentStatus.Closed) return 'CLOSED'
  return ''
}

function statusColor(status: IncidentStatus | undefined): string {
  if (status === IncidentStatus.Active) return 'text-status-on-scene'
  if (status === IncidentStatus.Suspended) return 'text-status-rtb'
  return 'text-outline'
}
</script>

<template>
  <header class="fixed top-0 left-0 right-0 h-12 z-50 flex items-center justify-between pr-4 bg-background border-b border-border">
    <!-- Left: brand zone + incident info -->
    <div class="flex items-center h-full overflow-hidden">
      <!-- Brand zone: w-20 matches sidebar width; arrow + logo centered within it -->
      <div class="w-20 flex items-center justify-center gap-2 shrink-0">
        <button
          class="flex items-center shrink-0 hover:opacity-80 transition-opacity focus:outline-none"
          aria-label="Back to incidents"
          @click="router.push({ name: 'incident-list' })"
        >
          <span class="material-symbols-outlined text-outline text-[18px]">arrow_back</span>
        </button>
        <img src="/assets/logo.svg" alt="First on Scene" class="h-8 w-auto shrink-0" />
      </div>

      <!-- Divider lands at x=80px, aligned with main content left bound -->
      <div class="h-4 w-px bg-border shrink-0" />

      <!-- Incident info starts at main content left bound -->
      <div class="flex items-center gap-2.5 pl-2.5 overflow-hidden">
        <input
          v-if="isEditingName"
          ref="nameInputEl"
          v-model="editingName"
          class="font-headline font-bold text-sm text-on-surface uppercase tracking-tight bg-surface-container border border-primary rounded px-1 w-[180px] focus:outline-none"
          @blur="commitEditName"
          @keydown="onNameKeydown"
        />
        <button
          v-else
          :class="[
            'font-headline font-bold text-sm text-on-surface uppercase tracking-tight truncate max-w-[120px] sm:max-w-[180px] focus:outline-none',
            canEditIncidentName ? 'hover:text-primary cursor-text' : 'cursor-default',
          ]"
          :title="canEditIncidentName ? 'Click to rename' : undefined"
          @click="startEditName"
        >
          {{ incident?.incident_name ?? '—' }}
        </button>

        <span
          v-if="incident?.status"
          :class="['hidden sm:inline font-mono text-[0.6rem] font-bold uppercase tracking-widest shrink-0', statusColor(incident.status)]"
        >
          {{ statusLabel(incident.status) }}
        </span>

        <div class="hidden sm:block h-4 w-px bg-border shrink-0" />

        <div class="hidden sm:flex">
          <HeaderOpSelector />
        </div>
      </div>
    </div>

    <!-- Right: IWI/PTI controls + actions -->
    <div class="flex items-center gap-2 shrink-0">
      <!-- Active PTI chip -->
      <button
        v-if="ptiStore.activePti"
        class="flex items-center gap-1.5 border border-amber-400 text-amber-700 dark:text-amber-400 px-2.5 py-1 rounded font-mono text-xs font-semibold uppercase tracking-wider hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-all"
        @click="navigateToOverview"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-amber-500 pti-dot-pulse shrink-0" />
        <span>Find</span>
        <span class="opacity-70">{{ ptiStore.activePti.declared_by_team_identifier }}</span>
        <span class="font-label tabular-nums text-[0.6rem] opacity-50">{{ ptiElapsed }}</span>
      </button>

      <!-- Active IWI chip -->
      <button
        v-if="iwiStore.activeIwi"
        class="flex items-center gap-1.5 bg-red-600 text-white px-2.5 py-1 rounded font-mono text-xs font-bold uppercase tracking-wider hover:brightness-110 transition-all iwi-chip-pulse"
        @click="navigateToOverview"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-white iwi-dot-pulse shrink-0" />
        <span>IWI</span>
        <span class="opacity-80">{{ iwiStore.activeIwi.declared_by_team_identifier }}</span>
        <span class="font-label tabular-nums">{{ iwiElapsed }}</span>
      </button>

      <!-- Manual declare button — shown when inside an incident, no active IWI, user is checked in -->
      <button
        v-else-if="isInsideIncident && personnelStore.myRecord"
        class="hidden sm:flex items-center gap-1 border border-border text-outline px-2.5 py-1 rounded font-mono text-xs font-bold uppercase tracking-wider hover:border-red-400 hover:text-red-600 transition-all"
        @click="showDeclareDialog = true"
      >
        <span class="material-symbols-outlined text-[14px]">emergency</span>
        Declare IWI
      </button>

      <!-- Self check-out — shown when inside an incident and user is checked in -->
      <button
        v-if="isInsideIncident && personnelStore.myRecord"
        class="hidden sm:flex items-center gap-1 border border-border text-outline px-2.5 py-1 rounded font-mono text-xs font-bold uppercase tracking-wider hover:border-amber-400 hover:text-amber-600 transition-all"
        @click="showCheckOutConfirm = true"
      >
        <span class="material-symbols-outlined text-[14px]">logout</span>
        Check Out
      </button>

      <div class="hidden sm:block"><ThemeToggle /></div>
      <UserAvatar />
    </div>
  </header>

  <CheckInModal
    v-if="incident"
    v-model:visible="showCheckIn"
    :incident-id="incident.incident_id"
  />

  <FosAlertDialog
    :open="showCheckOutConfirm"
    title="Check Out"
    :description="`Check yourself out of this incident?`"
    confirm-label="Check Out"
    cancel-label="Cancel"
    @update:open="(v) => { if (!v) showCheckOutConfirm = false }"
    @confirm="confirmSelfCheckOut"
  />

  <FosDialog
    :open="showDeclareDialog"
    title="Declare IWI"
    @update:open="showDeclareDialog = $event"
  >
    <p class="font-mono text-xs text-on-surface-variant mb-4">
      Use this when a team has reported an emergency via a channel not in the radio log (e.g. in person or by phone). Select the declaring team below.
    </p>
    <div class="flex flex-col gap-1 mb-2">
      <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Declaring Team</label>
      <FosSelect
        v-model="selectedTeamId"
        :options="teamOptions"
        placeholder="Select team..."
      />
    </div>
    <template #footer>
      <FosButton variant="secondary" @click="showDeclareDialog = false">Cancel</FosButton>
      <FosButton :disabled="!selectedTeamId || declaring" @click="confirmDeclareIwi">
        {{ declaring ? 'Declaring...' : 'Declare IWI' }}
      </FosButton>
    </template>
  </FosDialog>
</template>

<style scoped>
.iwi-chip-pulse {
  animation: chip-pulse 1.5s ease-in-out infinite;
}

@keyframes chip-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
  50% { box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.35); }
}

.iwi-dot-pulse {
  animation: dot-pulse 1.2s ease-in-out infinite;
}

@keyframes dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.75); }
}

.pti-dot-pulse {
  animation: dot-pulse 1.2s ease-in-out infinite;
}
</style>
